import React, { useEffect, useState } from "react"
import { Link } from "react-router"
import { useTranslation } from 'react-i18next'
import { useSelector } from "react-redux"
import { Dropdown } from 'react-bootstrap'
import Select from 'react-select'

import FilterSearch from 'pages/parts/FilterSearch'
import FilterPagination from 'pages/parts/FilterPagination'
import useFilterSearchPagination from 'pages/parts/useFilterSearchPagination'
import DocumentStatusHistory from 'pages/parts/DocumentStatusHistory'
import PeriodPicker from 'pages/parts/PeriodPicker'

import { httpOk, GET } from 'helpers/api'
import { formatDate, formatMoney, returnSign, findKeyFromArrayByValue } from "helpers/helpers"

function Index() {
	const { t } = useTranslation()
	const date = new Date()

	const reduxAccount = useSelector(state => state.account)

	const [data, setData] = useState({})
	const [statusHistoryId, setStatusHistoryId] = useState(null)
	const [poses, setPoses] = useState([])
	const [documentStatuses, setDocumentStatuses] = useState([])
	const [dropdownIsOpen, setDropdownIsOpen] = useState(false)
	const initialFilterData = {
		start_date: formatDate(new Date(date.getFullYear(), date.getMonth(), 1), 'yyyy-MM-dd'),
		end_date: formatDate(new Date(), 'yyyy-MM-dd'),
		pos_id: reduxAccount.pos_id,
		search: '',
	}
	const initialPagination = { url: '', response: null }
	const {
		filterData,
		appliedFilterData,
		filterDataExist,
		setFilterDataExist,
		pagination,
		setPagination,
		patchFilterData,
		applySearch,
		applyFilters,
		paginate,
		resetFilters,
	} = useFilterSearchPagination({ initialFilterData, initialPagination })

	async function searchWithFilter(customFilterData = filterData) {
		var filterUrl = "/write-off"
		var urlParams = ""
		checkFilter()

		var filterDataCopy = { ...customFilterData }

		if (filterDataCopy.pos_id)
			urlParams += returnSign(urlParams) + 'pos_id=' + filterDataCopy.pos_id
		if (filterDataCopy.document_status)
			urlParams += returnSign(urlParams) + 'document_status=' + filterDataCopy.document_status
		if (filterDataCopy.start_date)
			urlParams += returnSign(urlParams) + 'start_date=' + filterDataCopy.start_date
		if (filterData.end_date)
			urlParams += returnSign(urlParams) + 'end_date=' + filterDataCopy.end_date
		if (filterDataCopy.search)
			urlParams += returnSign(urlParams) + 'search=' + filterDataCopy.search
		if (filterDataCopy.page)
			urlParams += returnSign(urlParams) + 'page=' + filterDataCopy.page

		if (`/write-off${urlParams}` !== filterUrl)
			setFilterDataExist(true)

		filterUrl = filterUrl + urlParams

		const response = await GET(filterUrl, {}, { loader: true })
		if (httpOk(response)) {
			setData(response.data)
			setPagination({ ...pagination, 'url': filterUrl, 'response': response.data })
		}

		setDropdownIsOpen(false)
	}

	function resetFilter() {
		resetFilters({
			start_date: formatDate(new Date(date.getFullYear(), date.getMonth(), 1), 'yyyy-MM-dd'),
			end_date: formatDate(new Date(), 'yyyy-MM-dd'),
			pos_id: filterData.pos_id ?? '',
			search: '',
		}, searchWithFilter)
	}

	function checkFilter() {
		if (
			filterData.search === ''
		) {
			// managed by hook when filters are reset
		}
	}

	function toggleDropdown() {
		setDropdownIsOpen(!dropdownIsOpen)
	}

	function returnClassStatus(item) {
		if (!item.completed) {
			return "bg-secondary text-white"
		}
		if (item.completed) {
			return "bg-success text-white"
		}
	}

	async function getPoses() {
		const response = await GET('/helper/poses', {}, { loader: true })
		if (httpOk(response)) setPoses(response.data)
	}

	async function getDocumentStatuses() {
		const response = await GET('/helper/document-statuses?entity=write_off')
		if (httpOk(response)) setDocumentStatuses(response.data)
	}

	function handlePaginate(page) {
		const nextFilterData = { ...filterData, page }
		paginate(page)
		searchWithFilter(nextFilterData)
	}

	useEffect(() => {
		getPoses()
		getDocumentStatuses()
		searchWithFilter()
	}, []) // eslint-disable-line react-hooks/exhaustive-deps

	return (
		<>
			<div className="page-header d-flex justify-content-between">
				<h4 className="vertical-center">
					{t('write_off')} [{data.total}]
				</h4>
				<div className="d-flex">
					<Link className="btn-rounded btn btn-primary" to="/write-off/create">
						{t('create')}
					</Link>
				</div>
			</div>

			<div className="card">
				<div className="card-body">
					<div className="d-flex justify-content-between flex-wrap mb-3">
						<div className="d-flex flex-wrap gap-2">
							<div>
								<FilterSearch
									value={filterData.search}
									onSearchChange={(search) => applySearch(search, searchWithFilter)}>
								</FilterSearch>
							</div>
							<div>
								<Dropdown onToggle={toggleDropdown} show={dropdownIsOpen}>
									<Dropdown.Toggle variant="outline-primary" className="btn-wide h-100">
										{t('filter')}
									</Dropdown.Toggle>

									<Dropdown.Menu className="dropdown-filter">
										<div className="row">
											<div className="form-group col-md-6">
												<label>{t('pos')}</label>
												<Select
													options={poses}
													value={poses.find(option => option.id === filterData.pos_id) || ''}
													onChange={(option) => patchFilterData({ 'pos_id': option.id })}
													placeholder=""
													noOptionsMessage={() => t('list_empty')}
													getOptionLabel={(option) => option.name}
													getOptionValue={(option) => option.id}
												/>
											</div>
											<div className="form-group col-md-6">
												<label>{t('status')}</label>
												<Select
													isClearable
													options={documentStatuses}
													value={documentStatuses.find(option => option.value === filterData.document_status) || ''}
													onChange={(option) => patchFilterData({ 'document_status': option ? option.value : null })}
													placeholder=""
													noOptionsMessage={() => t('list_empty')}
													getOptionLabel={(option) => t(option.value)}
													getOptionValue={(option) => option.value}
												/>
											</div>
										</div>
										<div className="row">
											<div className="form-group col-md-12">
												<label>{t('period')}</label>
												<PeriodPicker
													startDate={filterData.start_date}
													endDate={filterData.end_date}
													onChange={(period) => patchFilterData(period)}
													withTime={false}
													minDate={new Date(new Date().getFullYear() - 1, 0, 1)}
													maxDate={new Date(new Date().getFullYear(), 11, 31)}
												/>
											</div>
										</div>
										<div className="row mt-3">
											<div className="col-md-12">
												<div className="d-flex justify-content-end gap-2">
													<button className="btn btn-secondary" onClick={() => setDropdownIsOpen(false)}>{t('cancel')}</button>
													<button className="btn btn-primary" onClick={() => applyFilters(searchWithFilter)}>{t('apply')}</button>
												</div>
											</div>
										</div>
									</Dropdown.Menu>
								</Dropdown>
							</div>
						</div>
						<div className="vertical-center">
							<h4><b>{t('amount')}:</b> {formatMoney(data?.total_cost_price)}</h4>
						</div>
					</div>

					{filterDataExist &&
						<div className="filter-block">
							<div className="filter">
								<strong>{t('filter')}</strong>
								<br />
								{(appliedFilterData.start_date || appliedFilterData.end_date) &&
									<span className="me-2">
										<b>{t('period')}: </b>
										{appliedFilterData.start_date &&
											<span>{formatDate(appliedFilterData.start_date)}</span>
										}
										{(appliedFilterData.start_date && appliedFilterData.end_date) &&
											<span>|</span>
										}
										{appliedFilterData.end_date &&
											<span>{formatDate(appliedFilterData.end_date)}</span>
										}
									</span>
								}
								{appliedFilterData.pos_id &&
									<span className="me-2">
										<b>{t('pos')}: </b>
										{findKeyFromArrayByValue(poses, 'id', appliedFilterData.pos_id, 'name')}
									</span>
								}
								{filterData.document_status &&
									<span className="me-2">
										<b>{t('status')}: </b>
										{t(filterData.document_status)}
									</span>
								}
								{appliedFilterData.search &&
									<span className="me-2">
										<b>{t('search')}: </b>
										<span>{appliedFilterData.search}</span>
									</span>
								}
							</div>
							<div className="vertical-center">
								<button className="btn btn-outline-dark text-uppercase" onClick={resetFilter}>{t('reset')}</button>
							</div>
						</div>
					}

					<div className="table-responsive">
						<table className="table table-bordered mb-0">
							<thead>
								<tr>
									<th style={{ 'minWidth': '33px' }} className="text-center">#</th>
									<th style={{ 'width': '15%' }}>{t('pos')}</th>
									<th style={{ 'width': '20%' }}>{t('wms')}</th>
									<th style={{ 'width': '25%' }}>{t('note')}</th>
									<th style={{ 'width': '10%' }} className="text-center">{t('document')}</th>
									<th style={{ 'width': '10%' }} className="text-center">{t('date')}</th>
									<th style={{ 'width': '10%' }} className="text-center">{t('status')}</th>
									<th style={{ 'width': '10%' }} className="text-end">{t('amount')}</th>
									<th style={{ 'minWidth': '33px' }}></th>
								</tr>
							</thead>
							<tbody>
								{data?.data?.map((item, index) => (
									<tr key={index}>
										<td className="text-center">{item.row_number}</td>
										<td>{item.pos_name}</td>
										<td>{item.wms_name}</td>
										<td>{item.note}</td>
										<td className="text-center">{item.document_number}</td>
										<td className="text-center">{formatDate(item.created_at, 'dd.MM.yy HH:mm')}</td>
										<td className={`text-center ${returnClassStatus(item)}`}>
											{!item.completed ?
												<span>{t('expected')}</span>
												:
												<span>{t('closed')}</span>
											}
										</td>
										<td className="text-end">{formatMoney(item.total_cost_price)}</td>
										<td className="d-flex justify-content-center">
											<div className="table-dropdown">
												<Dropdown id={`dropdown-${index}`}>
													<Dropdown.Toggle variant="outline-primary">
														<i className="uil uil-ellipsis-v"></i>
													</Dropdown.Toggle>

													<Dropdown.Menu>
														{item.completed ?
															<Link to={`/write-off/preview/${item.id}`} className="table-dropdown-item">
																<i className="uil-eye"></i>
																{t('preview')}
															</Link>
															:
															<Link to={`/write-off/update/${item.id}`} className="table-dropdown-item">
																<i className="uil-edit-alt"></i>
																{t('edit')}
															</Link>
														}
														<div className="table-dropdown-item"
															onClick={() => setStatusHistoryId(item.id)}>
															<i className="uil-history"></i>
															{t('document_status_history')}
														</div>
													</Dropdown.Menu>
												</Dropdown>
											</div>
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>

					{pagination.response &&
						<FilterPagination
							pagination={pagination}
							paginate={handlePaginate}>
						</FilterPagination>
					}
				</div>
			</div>

			<DocumentStatusHistory
				entity="write_off"
				documentId={statusHistoryId}
				setDocumentId={setStatusHistoryId}>
			</DocumentStatusHistory>
		</>
	)
}

export default Index